import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Calendar } from 'lucide-react';
import api from '../../../services/api';
import DocumentsTab from '../../../components/Employee/DocumentsTab';
import SalaryTab from '../../../components/Employee/SalaryTab';
import HybridScheduleModal from '../../../components/HybridScheduleModal';

const EmployeeProfileModal = ({ employee, onClose, onEdit }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(true);
  const [isHybridOpen, setIsHybridOpen] = useState(false);

  useEffect(() => {
    let activeUrl = null;
    const fetchPhoto = async () => {
      if (!employee) return;
      try {
        setLoadingPhoto(true);
        // First get documents to find the PHOTO document ID
        const docsRes = await api.get(`/employees/${employee._id}/documents`);
        const photoItem = docsRes.data.find(item => item.type?.code === 'PHOTO' && item.document);
        
        if (photoItem) {
          // Fetch the photo content as a blob
          const res = await api.get(`/employees/${employee._id}/documents/${photoItem.document._id}/download`, { responseType: 'blob' });
          const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
          activeUrl = url;
          setPhotoUrl(url);
        }
      } catch (err) {
        console.error('Failed to fetch profile picture:', err);
      } finally {
        setLoadingPhoto(false);
      }
    };

    fetchPhoto();
    
    // Cleanup blob url on unmount
    return () => {
      if (activeUrl) window.URL.revokeObjectURL(activeUrl);
    };
  }, [employee]);

  useEffect(() => {
    if (employee) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [employee]);

  if (!employee) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100]">
      {/* Separate blur layer so dark mode extensions don't break the color */}
      <div className="absolute inset-0 backdrop-blur-md bg-transparent"></div>
      {/* Separate color layer */}
      <div className="absolute inset-0 bg-black/70"></div>
      
      {/* Scrollable container */}
      <div className="absolute inset-0 overflow-y-auto py-10 px-4">
        <div className="relative mx-auto w-full max-w-4xl shadow-2xl rounded-2xl bg-gray-50 overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 h-32 relative">
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-white hover:text-gray-200 bg-black bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        {/* Profile Info Section */}
        <div className="px-8 pb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-end -mt-16 mb-8 gap-6">
            <div className="relative h-32 w-32 rounded-full border-4 border-white shadow-lg bg-white overflow-hidden flex-shrink-0 flex items-center justify-center">
              {loadingPhoto ? (
                <div className="text-gray-400 text-sm">Loading...</div>
              ) : photoUrl ? (
                <img src={photoUrl} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full bg-indigo-100 text-indigo-500 flex items-center justify-center text-4xl font-bold">
                  {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                </div>
              )}
            </div>
            
            <div className="flex-1 text-center sm:text-left mb-2">
              <h2 className="text-2xl font-bold text-gray-900">{employee.firstName} {employee.lastName}</h2>
              <p className="text-sm text-gray-500 font-medium">{employee.designation || 'No Designation'} • {employee.department?.name || 'No Department'}</p>
            </div>
            
            <div className="mb-2">
              <span className={`px-4 py-1.5 inline-flex text-sm font-semibold rounded-full shadow-sm border
                ${employee.role === 'Admin' ? 'bg-purple-50 text-purple-700 border-purple-200' : 
                  employee.role === 'HR' ? 'bg-blue-50 text-blue-700 border-blue-200' : 
                  'bg-white text-gray-700 border-gray-200'}`}>
                {employee.role}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 border-b pb-2 gap-4 sm:gap-0">
              <h3 className="text-lg font-semibold text-gray-800">Employee Details</h3>
              <div className="flex gap-4 items-center">
                {employee.workLocation === 'HYBRID' && (
                  <button 
                    onClick={() => setIsHybridOpen(true)}
                    className="text-sm font-medium text-purple-600 hover:text-purple-800 flex items-center gap-1.5 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-lg border border-purple-200 transition"
                  >
                    <Calendar className="w-4 h-4 text-purple-600" />
                    Hybrid Schedule
                  </button>
                )}
                <button 
                  onClick={() => onEdit(employee)}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                  Edit Details
                </button>
                <button
                  onClick={async () => {
                    const willActivate = !employee.isActive;
                    const confirmMsg = willActivate
                      ? `Are you sure you want to activate/restore ${employee.firstName} ${employee.lastName}?`
                      : `Are you sure you want to deactivate ${employee.firstName} ${employee.lastName}?`;
                    if (window.confirm(confirmMsg)) {
                      try {
                        await api.put(`/employees/${employee._id}`, { isActive: willActivate });
                        window.location.reload();
                      } catch (err) {
                        console.error('Failed to update employee status:', err);
                        alert(err.response?.data?.message || 'Failed to update employee status');
                      }
                    }
                  }}
                  className={`text-sm font-medium flex items-center gap-1 ${
                    employee.isActive ? 'text-amber-600 hover:text-amber-800' : 'text-emerald-600 hover:text-emerald-800'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {employee.isActive ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                  {employee.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button 
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to permanently delete this employee? This action cannot be undone.')) {
                      try {
                        await api.delete(`/employees/${employee._id}`);
                        window.location.reload();
                      } catch (error) {
                        console.error('Failed to delete employee:', error);
                      }
                    }
                  }}
                  className="text-sm font-medium text-red-600 hover:text-red-800 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                  Delete
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Employee ID</p>
                <p className="font-medium text-gray-900">{employee.employeeId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Email Address</p>
                <p className="font-medium text-gray-900">{employee.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Work Location</p>
                <p className="font-medium text-gray-900">
                  <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    employee.workLocation === 'HYBRID' 
                      ? 'bg-purple-100 text-purple-800' 
                      : employee.workLocation === 'WFH' 
                        ? 'bg-amber-100 text-amber-800' 
                        : 'bg-emerald-100 text-emerald-800'
                  }`}>
                    {employee.workLocation === 'HYBRID' ? 'Hybrid' : employee.workLocation === 'WFH' ? 'Remote (WFH)' : 'Office'}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Account Status</p>
                <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${employee.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {employee.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Reports To</p>
                <p className="font-medium text-gray-900">
                  {employee.managerId ? `${employee.managerId.firstName} ${employee.managerId.lastName}` : 'No Manager'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
             {/* Note: DocumentsTab has its own padding/styling, we wrap it cleanly */}
            <DocumentsTab employeeId={employee._id} />
          </div>
          
          <SalaryTab employeeId={employee._id} />
        </div>
      </div>
    </div>
    {isHybridOpen && (
      <HybridScheduleModal
        isOpen={isHybridOpen}
        employee={employee}
        onClose={() => setIsHybridOpen(false)}
        onSaved={() => {}}
      />
    )}
    </div>,
    document.body
  );
};

export default EmployeeProfileModal;
